import { UpdateWorkspaceDto, WorkspaceDto } from "@growth/dto";
import { slugify } from "@growth/schema";
import { Badge, Button, Field, Input } from "@growth/ui";
import { LoaderCircle, Plus, Save } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import {
  useBrands,
  useCreateBrand,
  useUpdateWorkspace,
  useWorkspaceSettings,
} from "../lib/workspace";

/** The workspace's name, time zone, spending limits, pause switch, and its brands. */
export const SettingsPage = () => {
  const { workspace } = useActive();
  const settings = useWorkspaceSettings();

  return (
    <section className="max-w-3xl space-y-10">
      <PageHeader eyebrow={workspace?.name ?? ""} title="Settings" />
      {settings.isError && <ErrorText>{apiErrorMessage(settings.error)}</ErrorText>}
      {settings.data && <WorkspaceForm key={settings.data.id} settings={settings.data} />}
      <Brands />
    </section>
  );
};

const money = (value: number) => `US$${value.toFixed(2)}`;

const WorkspaceForm = ({ settings }: { settings: WorkspaceDto }) => {
  const { atLeast } = useActive();
  const isOwner = atLeast("OWNER");
  const update = useUpdateWorkspace();
  const [form, setForm] = useState({
    name: settings.name,
    timeZone: settings.timeZone,
    monthlyCapUsd: String(settings.monthlyCapUsd),
    runCapUsd: String(settings.runCapUsd),
    weeklyAllowanceUsd: String(settings.weeklyAllowanceUsd),
    prospectingCapUsd: String(settings.prospectingCapUsd),
    maxConcurrentRuns: String(settings.maxConcurrentRuns),
  });
  const set = (key: keyof typeof form, value: string) => setForm({ ...form, [key]: value });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const input: UpdateWorkspaceDto = {
      name: form.name,
      timeZone: form.timeZone,
      monthlyCapUsd: Number(form.monthlyCapUsd),
      runCapUsd: Number(form.runCapUsd),
      weeklyAllowanceUsd: Number(form.weeklyAllowanceUsd),
      prospectingCapUsd: Number(form.prospectingCapUsd),
      maxConcurrentRuns: Number(form.maxConcurrentRuns),
    };
    update.mutate(input);
  };

  const used =
    settings.monthlyCapUsd > 0 ? Math.min(1, settings.monthSpentUsd / settings.monthlyCapUsd) : 0;

  const numberField = (key: keyof typeof form, label: string, hint: string) => (
    <Field label={label} hint={hint}>
      {(props) => (
        <Input
          {...props}
          type="number"
          min={0}
          step={key === "maxConcurrentRuns" ? 1 : 0.5}
          required
          disabled={!isOwner}
          value={form[key]}
          onChange={(event) => set(key, event.target.value)}
        />
      )}
    </Field>
  );

  return (
    <form onSubmit={submit} className="space-y-5 rounded-xl border border-line bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-lg text-ink">Workspace</h2>
        {settings.paused ? (
          <Badge>Agents paused</Badge>
        ) : (
          <Badge tone="success">Agents running</Badge>
        )}
      </div>

      <div>
        <p className="text-sm">
          Spent this month: <strong>{money(settings.monthSpentUsd)}</strong> of{" "}
          {money(settings.monthlyCapUsd)}
        </p>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-paper" aria-hidden>
          <div className="h-full rounded-full bg-accent" style={{ width: `${used * 100}%` }} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name">
          {(props) => (
            <Input
              {...props}
              required
              disabled={!isOwner}
              value={form.name}
              onChange={(event) => set("name", event.target.value)}
            />
          )}
        </Field>
        <Field label="Time zone" hint="Calendars and weeks follow this">
          {(props) => (
            <Input
              {...props}
              required
              disabled={!isOwner}
              value={form.timeZone}
              onChange={(event) => set("timeZone", event.target.value)}
            />
          )}
        </Field>
        {numberField(
          "monthlyCapUsd",
          "Monthly cap (US$)",
          "Agents stop when content spend reaches this",
        )}
        {numberField("runCapUsd", "Per-run cap (US$)", "Most one piece may cost")}
        {numberField(
          "weeklyAllowanceUsd",
          "Director's weekly allowance (US$)",
          "What it may start without asking",
        )}
        {numberField("prospectingCapUsd", "Prospecting budget (US$)", "Separate from content")}
        {numberField("maxConcurrentRuns", "Agents at once", "1–10")}
      </div>

      {update.isError && <ErrorText>{apiErrorMessage(update.error)}</ErrorText>}
      {isOwner ? (
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            Save
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => update.mutate({ paused: !settings.paused })}
          >
            {settings.paused ? "Resume agents" : "Pause all agents"}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted">Only owners can change these.</p>
      )}
    </form>
  );
};

const Brands = () => {
  const { atLeast } = useActive();
  const brands = useBrands();
  const create = useCreateBrand();
  const [form, setForm] = useState({ name: "", slug: "", codePrefix: "", websiteUrl: "" });
  const [slugTouched, setSlugTouched] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        name: form.name,
        slug: form.slug,
        codePrefix: form.codePrefix,
        websiteUrl: form.websiteUrl.trim() || null,
      },
      {
        onSuccess: () => {
          setForm({ name: "", slug: "", codePrefix: "", websiteUrl: "" });
          setSlugTouched(false);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-lg text-ink">Brands</h2>
      <ul className="divide-y divide-line rounded-xl border border-line bg-white">
        {brands.data?.length === 0 && (
          <li className="px-4 py-6 text-sm text-muted">No brands yet.</li>
        )}
        {brands.data?.map((brand) => (
          <li
            key={brand.id}
            className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
          >
            <span>
              <strong className="font-medium">{brand.name}</strong>{" "}
              <span className="text-muted">
                · {brand.slug} · pieces {brand.codePrefix}-…
                {brand.websiteUrl && ` · ${brand.websiteUrl}`}
              </span>
            </span>
            {brand.hasBlog ? <Badge tone="accent">Blog</Badge> : <Badge>No blog</Badge>}
          </li>
        ))}
      </ul>

      {atLeast("OWNER") && (
        <form onSubmit={submit} className="space-y-3 rounded-xl border border-line bg-white p-5">
          <h3 className="text-sm font-semibold">Add a brand</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name">
              {(props) => (
                <Input
                  {...props}
                  required
                  value={form.name}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      name: event.target.value,
                      slug: slugTouched ? form.slug : slugify(event.target.value),
                    })
                  }
                />
              )}
            </Field>
            <Field label="Address" hint="Lower case and hyphens">
              {(props) => (
                <Input
                  {...props}
                  required
                  value={form.slug}
                  onChange={(event) => {
                    setSlugTouched(true);
                    setForm({ ...form, slug: event.target.value });
                  }}
                />
              )}
            </Field>
            <Field label="Code prefix" hint="2–4 letters for piece ids, e.g. fr → fr-li-0001">
              {(props) => (
                <Input
                  {...props}
                  required
                  maxLength={4}
                  value={form.codePrefix}
                  onChange={(event) => setForm({ ...form, codePrefix: event.target.value })}
                />
              )}
            </Field>
            <Field label="Website (optional)">
              {(props) => (
                <Input
                  {...props}
                  type="url"
                  placeholder="https://"
                  value={form.websiteUrl}
                  onChange={(event) => setForm({ ...form, websiteUrl: event.target.value })}
                />
              )}
            </Field>
          </div>
          {create.isError && <ErrorText>{apiErrorMessage(create.error)}</ErrorText>}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
            Add brand
          </Button>
        </form>
      )}
    </div>
  );
};
